import React, { useState, useRef, useEffect, useCallback } from 'react';
import { IconButton, Tooltip, Box, Typography, CircularProgress, alpha } from '@mui/material';
import { Mic, MicOff, Stop, VolumeUp } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import useWebSocketContext from '../hooks/useWebSocketContext';
import { useAuth0 } from '@auth0/auth0-react';
import store from '../../store/store'; // Import store for retry mechanism
import {
  setVoiceState,
  setTranscriptionText,
  setVoiceError,
  setStatusMessage,
  setVoiceSupported,
  resetVoice,
  clearTranscription,
  VoiceState,
  selectVoiceState,
  selectTranscriptionText,
  selectVoiceError,
  selectStatusMessage,
  selectIsVoiceSupported,
} from '../../store/slices/voiceSlice';
import { selectOriginalSessionId } from '../../store/slices/sessionSlice';
import { setUser } from '../../store/slices/sessionSlice';
import { useTheme } from '@mui/material/styles';

interface VoiceButtonProps {
  onTranscriptionComplete: (text: string) => void;
  disabled?: boolean;
}

const VoiceButton: React.FC<VoiceButtonProps> = ({ onTranscriptionComplete, disabled = false }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { sendToWs, sendBinaryToWs } = useWebSocketContext();
  const { user: auth0User, isAuthenticated: auth0IsAuthenticated } = useAuth0();
  
  // Redux state
  const voiceState = useSelector((state: RootState) => selectVoiceState(state));
  const transcriptionText = useSelector((state: RootState) => selectTranscriptionText(state));
  const voiceError = useSelector((state: RootState) => selectVoiceError(state));
  const statusMessage = useSelector((state: RootState) => selectStatusMessage(state));
  const isVoiceSupported = useSelector((state: RootState) => selectIsVoiceSupported(state));
  const session = useSelector((state: RootState) => state.session);
  const originalSessionId = useSelector(selectOriginalSessionId);
  const selectedModel = useSelector((state: RootState) => state.model.selectedModel);

  // Authentication recovery function
  const recoverAuthentication = useCallback(() => {
    console.log('🔐 VoiceButton: Attempting authentication recovery');
    
    if (auth0IsAuthenticated && auth0User?.sub && auth0User?.name) {
      console.log('🔐 VoiceButton: Recovering authentication from Auth0:', {
        auth0UserId: auth0User.sub,
        auth0UserName: auth0User.name,
        currentSessionUserId: session.user_id
      });
      
      dispatch(setUser({
        id: auth0User.sub,
        user_name: auth0User.name,
        email: auth0User.email || null
      }));
      
      return auth0User.sub;
    }
    
    console.warn('🔐 VoiceButton: Authentication recovery failed - no Auth0 user available');
    return null;
  }, [auth0IsAuthenticated, auth0User, session.user_id, dispatch]);
  
  // Local state
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [isListening, setIsListening] = useState(false);
  const audioChunksRef = useRef<Blob[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // WebSocket message handler
  useEffect(() => {
    const handleVoiceMessage = (event: MessageEvent) => {
      console.log('🎤 VoiceButton: Received voice-message event:', event);
      try {
        const data = JSON.parse(event.data);
        console.log('🎤 VoiceButton: Parsed voice message data:', data);
        
        if (data.method === 'voice_status') {
          console.log('🎤 VoiceButton: Handling voice_status:', data.status);
          switch (data.status) {
            case 'loading':
              console.log('🎤 VoiceButton: Setting state to loading');
              dispatch(setVoiceState('loading'));
              dispatch(setStatusMessage(data.message));
              break;
            case 'ready':
              console.log('🎤 VoiceButton: Voice ready, starting recording...');
              dispatch(setVoiceState('ready'));
              dispatch(setStatusMessage(data.message));
              // Add a small delay to ensure state is updated before starting recording
              setTimeout(() => {
                console.log('🎤 VoiceButton: Delayed recording start triggered');
                startRecording();
              }, 100);
              break;
            case 'error':
              console.log('🎤 VoiceButton: Voice status error:', data.message);
              dispatch(setVoiceError(data.message));
              dispatch(setStatusMessage('Voice recognition failed'));
              break;
            default:
              console.warn('🎤 VoiceButton: Unknown voice status:', data.status);
          }
        } else if (data.method === 'voice_transcription') {
          console.log('🎤 VoiceButton: Handling voice_transcription:', {
            transcribed_text: data.transcribed_text,
            user_id: data.user_id
          });
          const transcribedText = data.transcribed_text || '';
          console.log('🎤 VoiceButton: Setting transcription text:', transcribedText);
          dispatch(setTranscriptionText(transcribedText));
          dispatch(setVoiceState('processing'));
          
          // Immediately show transcription in input field for real-time effect
          if (transcribedText.trim()) {
            console.log('🎤 VoiceButton: Calling onTranscriptionComplete with:', transcribedText);
            onTranscriptionComplete(transcribedText);
            
            // Reset after transcription is sent (longer delay to show user feedback)
            setTimeout(() => {
              console.log('🎤 VoiceButton: Resetting voice state');
              dispatch(resetVoice());
            }, 2000); // Increased delay to allow user to see what was transcribed
          } else {
            console.warn('🎤 VoiceButton: Empty transcription text received');
            // Reset immediately if no transcription
            dispatch(resetVoice());
          }
        } else if (data.method === 'voice_error') {
          console.log('🎤 VoiceButton: Handling voice_error:', data.message);
          dispatch(setVoiceError(data.message));
          stopRecording();
        }
      } catch (error) {
        console.error('🎤 VoiceButton: Error parsing voice message:', error);
      }
    };

    // Listen for voice messages dispatched by WebSocket service
    window.addEventListener('voice-message', handleVoiceMessage as EventListener);
    
    return () => {
      window.removeEventListener('voice-message', handleVoiceMessage as EventListener);
    };
  }, [dispatch, onTranscriptionComplete]);

  // Check browser support on mount
  useEffect(() => {
    const isSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    dispatch(setVoiceSupported(isSupported));
    
    if (!isSupported) {
      dispatch(setVoiceError('Voice input is not supported in this browser'));
    }
  }, [dispatch]);

  const initializeVoiceRecognition = useCallback(async () => {
    let currentUserId = session.user_id;
    
    // Attempt authentication recovery if user_id is missing
    if (!currentUserId) {
      console.warn('🎤 VoiceButton: user_id missing during initialization, attempting recovery...');
      currentUserId = recoverAuthentication();
    }
    
    if (!isVoiceSupported || !originalSessionId || !currentUserId) {
      console.error('🎤 VoiceButton: Voice initialization failed:', {
        isVoiceSupported,
        originalSessionId,
        currentUserId,
        sessionState: session
      });
      dispatch(setVoiceError('Voice recognition not available - please ensure you are logged in'));
      return;
    }

    try {
      dispatch(setVoiceState('loading'));
      dispatch(setStatusMessage('Initializing voice recognition...'));
      dispatch(clearTranscription());

      // Send voice initialization request to backend using original session ID
      const initMessage = JSON.stringify({
        method: 'init_voice',
        params: {
          session_id: originalSessionId,
          user_id: currentUserId,
        },
      });
      
      console.log('🎤 VoiceButton: Sending voice initialization:', {
        session_id: originalSessionId,
        user_id: currentUserId
      });
      
      sendToWs(initMessage);
      
    } catch (error) {
      console.error('Voice initialization failed:', error);
      dispatch(setVoiceError('Failed to initialize voice recognition'));
    }
  }, [isVoiceSupported, originalSessionId, session.user_id, sendToWs, dispatch, recoverAuthentication]);

  const startRecording = useCallback(async () => {
    console.log('🎤 VoiceButton: startRecording called, current voiceState:', voiceState);
    try {
      // Check if browser supports required APIs
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Browser does not support getUserMedia API');
      }

      if (!window.MediaRecorder) {
        throw new Error('Browser does not support MediaRecorder API');
      }

      console.log('🎤 VoiceButton: Requesting microphone access...');
      
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
        },
      });

      console.log('🎤 VoiceButton: Microphone access granted, stream:', stream);
      setMediaStream(stream);
      audioChunksRef.current = [];

      // Check MediaRecorder compatibility
      const supportedTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/wav'
      ];
      
      let mimeType = 'audio/webm;codecs=opus'; // default
      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          console.log('🎤 VoiceButton: Using supported MIME type:', mimeType);
          break;
        }
      }

      if (!MediaRecorder.isTypeSupported(mimeType)) {
        console.warn('🎤 VoiceButton: Selected MIME type not supported, using default');
      }

      // Create MediaRecorder
      const recorder = new MediaRecorder(stream, {
        mimeType: mimeType,
      });

      console.log('🎤 VoiceButton: MediaRecorder created, state:', recorder.state);

      recorder.ondataavailable = (event) => {
        console.log('🎤 VoiceButton: Data available, size:', event.data.size);
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          console.log('🎤 VoiceButton: Audio chunks count:', audioChunksRef.current.length);
        }
      };

      recorder.onstop = () => {
        console.log('🎤 VoiceButton: MediaRecorder stopped, chunks:', audioChunksRef.current.length);
        handleRecordingComplete();
      };

      recorder.onerror = (event) => {
        console.error('🎤 VoiceButton: MediaRecorder error:', event);
        dispatch(setVoiceError('Recording error occurred'));
        stopRecording();
      };

      recorder.onstart = () => {
        console.log('🎤 VoiceButton: MediaRecorder started successfully');
      };

      console.log('🎤 VoiceButton: Starting MediaRecorder...');
      recorder.start(100); // Collect data every 100ms
      
      setMediaRecorder(recorder);
      setIsListening(true);
      dispatch(setVoiceState('recording'));
      dispatch(setStatusMessage('Listening... Click to stop'));

      console.log('🎤 VoiceButton: Recording state updated, setting 30s timeout');
      
      // Auto-stop after 30 seconds
      timeoutRef.current = setTimeout(() => {
        console.log('🎤 VoiceButton: 30s timeout reached, stopping recording');
        stopRecording();
      }, 30000);

    } catch (error) {
      console.error('🎤 VoiceButton: Failed to start recording:', error);
      
      let errorMessage = 'Failed to access microphone';
      
      if (error instanceof Error) {
        console.error('🎤 VoiceButton: Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Microphone permission denied. Please allow microphone access and try again.';
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'No microphone found. Please connect a microphone and try again.';
        } else if (error.name === 'NotSupportedError') {
          errorMessage = 'Your browser does not support voice recording.';
        } else if (error.message) {
          errorMessage = error.message;
        }
      } else if (error && typeof error === 'object' && 'name' in error && 'message' in error) {
        const domError = error as DOMException;
        if (domError.name === 'NotAllowedError') {
          errorMessage = 'Microphone permission denied. Please allow microphone access and try again.';
        } else if (domError.name === 'NotFoundError') {
          errorMessage = 'No microphone found. Please connect a microphone and try again.';
        } else if (domError.name === 'NotSupportedError') {
          errorMessage = 'Your browser does not support voice recording.';
        } else if (domError.message) {
          errorMessage = domError.message;
        }
      }
      
      dispatch(setVoiceError(errorMessage));
      stopRecording();
    }
  }, [dispatch, voiceState]);

  const stopRecording = useCallback(() => {
    console.log('🎤 VoiceButton: stopRecording called, mediaRecorder state:', mediaRecorder?.state);
    console.log('🎤 VoiceButton: Current audio chunks count:', audioChunksRef.current?.length || 0);
    
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      console.log('🎤 VoiceButton: Stopping MediaRecorder...');
      mediaRecorder.stop();
    } else {
      console.log('🎤 VoiceButton: MediaRecorder already inactive or null');
    }
    
    if (mediaStream) {
      console.log('🎤 VoiceButton: Stopping media stream tracks...');
      mediaStream.getTracks().forEach(track => {
        console.log('🎤 VoiceButton: Stopping track:', track.kind, track.readyState);
        track.stop();
      });
      setMediaStream(null);
    }
    
    if (timeoutRef.current) {
      console.log('🎤 VoiceButton: Clearing timeout...');
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    setIsListening(false);
    setMediaRecorder(null);
    dispatch(setVoiceState('transcribing'));
    dispatch(setStatusMessage('Processing audio...'));
    console.log('🎤 VoiceButton: stopRecording completed');
  }, [mediaRecorder, mediaStream, dispatch]);

  const handleRecordingComplete = useCallback(() => {
    console.log('🎤 VoiceButton: handleRecordingComplete called');
    console.log('🎤 VoiceButton: Audio chunks available:', audioChunksRef.current.length);
    
    // Add detailed debugging of session state
    console.log('🎤 VoiceButton: Session state debug:', {
      user_id: session.user_id,
      user_name: session.user_name,
      session_id: session.session_id,
      original_session_id: session.original_session_id,
      isLoggedIn: session.isLoggedIn,
      originalSessionId: originalSessionId
    });
    
    if (audioChunksRef.current.length === 0) {
      console.warn('🎤 VoiceButton: No audio chunks recorded');
      dispatch(setVoiceError('No audio recorded'));
      dispatch(resetVoice());
      return;
    }

    // Validate that user is authenticated before processing audio
    // Try multiple sources for user_id to handle edge cases
    let currentUserId = session.user_id;
    
    // If user_id is missing, attempt authentication recovery
    if (!currentUserId) {
      console.warn('🎤 VoiceButton: user_id missing, attempting recovery...');
      currentUserId = recoverAuthentication();
      
      // Give recovery a moment to take effect and retry with current state
      if (!currentUserId) {
        console.warn('🎤 VoiceButton: Recovery failed, will retry in 100ms');
        setTimeout(() => {
          // Get fresh session state for retry
          const currentState = store.getState();
          const freshSession = currentState.session;
          
          if (freshSession.user_id && originalSessionId) {
            console.log('🎤 VoiceButton: Authentication recovered after delay, retrying recording completion');
            // Manually create the audio processing with recovered state
            const metadata = JSON.stringify({
              method: 'voice_audio',
              params: {
                session_id: originalSessionId,
                user_id: freshSession.user_id,
                model_name: selectedModel,
              },
            });
            
            // Process the same audio chunks that were collected
            if (audioChunksRef.current.length > 0) {
              const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
              const metadataBytes = new TextEncoder().encode(metadata);
              const delimiter = new Uint8Array([0]);
              
              audioBlob.arrayBuffer().then(audioBuffer => {
                const audioBytes = new Uint8Array(audioBuffer);
                const combinedArray = new Uint8Array(metadataBytes.length + delimiter.length + audioBytes.length);
                
                combinedArray.set(metadataBytes, 0);
                combinedArray.set(delimiter, metadataBytes.length);
                combinedArray.set(audioBytes, metadataBytes.length + delimiter.length);
                
                console.log('🎤 VoiceButton: Retry - sending audio with recovered auth');
                sendBinaryToWs(combinedArray);
                audioChunksRef.current = []; // Clear chunks after successful send
              });
            }
          } else {
            console.error('🎤 VoiceButton: Recovery retry failed - still no authentication');
            dispatch(setVoiceError('Authentication recovery failed. Please refresh and try again.'));
            dispatch(resetVoice());
          }
        }, 100);
        return; // Exit early if we're retrying
      }
    }
    
    const isAuthenticated = currentUserId && originalSessionId;
    
    console.log('🎤 VoiceButton: Authentication check:', {
      currentUserId,
      originalSessionId,
      isAuthenticated,
      sessionState: session,
      recoveryAttempted: !session.user_id && currentUserId
    });
    
    if (!isAuthenticated) {
      console.error('🎤 VoiceButton: User not authenticated:', { 
        user_id: currentUserId, 
        originalSessionId: originalSessionId,
        fullSession: session
      });
      
      // Give more specific error messages based on what's missing
      let errorMessage = 'User authentication required for voice transcription';
      if (!currentUserId && !originalSessionId) {
        errorMessage = 'Please log in and ensure you have an active session before using voice features';
      } else if (!currentUserId) {
        errorMessage = 'User authentication lost. Please refresh the page and try again';
      } else if (!originalSessionId) {
        errorMessage = 'Session not initialized. Please refresh the page and try again';
      }
      
      dispatch(setVoiceError(errorMessage));
      dispatch(resetVoice());
      return;
    }

    try {
      console.log('🎤 VoiceButton: Creating audio blob from chunks...');
      
      // Combine all audio chunks
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      
      console.log('🎤 VoiceButton: Audio blob created:', {
        size: audioBlob.size,
        type: audioBlob.type
      });
      
      // Validate audio size
      if (audioBlob.size < 1000) {
        console.warn('🎤 VoiceButton: Audio blob too small:', audioBlob.size);
        dispatch(setVoiceError('Audio recording too short. Please try again.'));
        dispatch(resetVoice());
        return;
      }
      
      if (audioBlob.size > 25 * 1024 * 1024) { // 25MB limit
        console.warn('🎤 VoiceButton: Audio blob too large:', audioBlob.size);
        dispatch(setVoiceError('Audio recording too large. Please record shorter audio.'));
        dispatch(resetVoice());
        return;
      }
      
      console.log(`🎤 VoiceButton: Sending audio blob: ${audioBlob.size} bytes, type: ${audioBlob.type}`);
      
      // Send audio to backend for transcription using original session ID
      const metadata = JSON.stringify({
        method: 'voice_audio',
        params: {
          session_id: originalSessionId,
          user_id: currentUserId, // Use the recovered user ID, not session.user_id
          model_name: selectedModel,
        },
      });
      
      console.log('🎤 VoiceButton: Audio metadata:', metadata);

      // Create binary message with metadata
      const metadataBytes = new TextEncoder().encode(metadata);
      const delimiter = new Uint8Array([0]);
      
      console.log('🎤 VoiceButton: Processing audio blob to array buffer...');
      
      audioBlob.arrayBuffer().then(audioBuffer => {
        console.log('🎤 VoiceButton: Audio buffer created, size:', audioBuffer.byteLength);
        
        const audioBytes = new Uint8Array(audioBuffer);
        const combinedArray = new Uint8Array(metadataBytes.length + delimiter.length + audioBytes.length);
        
        combinedArray.set(metadataBytes, 0);
        combinedArray.set(delimiter, metadataBytes.length);
        combinedArray.set(audioBytes, metadataBytes.length + delimiter.length);
        
        console.log(`🎤 VoiceButton: Sending combined binary data: ${combinedArray.length} bytes`);
        console.log('🎤 VoiceButton: Breakdown - metadata: ${metadataBytes.length}, delimiter: 1, audio: ${audioBytes.length}');
        
        sendBinaryToWs(combinedArray);
        console.log('🎤 VoiceButton: Binary data sent successfully');
      }).catch(error => {
        console.error('🎤 VoiceButton: Error processing audio blob:', error);
        dispatch(setVoiceError('Failed to process audio. Please try again.'));
        dispatch(resetVoice());
      });

    } catch (error) {
      console.error('🎤 VoiceButton: Error in handleRecordingComplete:', error);
      dispatch(setVoiceError('Failed to process recording. Please try again.'));
      dispatch(resetVoice());
    } finally {
      console.log('🎤 VoiceButton: Clearing audio chunks array');
      audioChunksRef.current = [];
    }
  }, [session.user_id, originalSessionId, selectedModel, sendBinaryToWs, dispatch, recoverAuthentication]);

  const handleVoiceButtonClick = useCallback(() => {
    if (disabled) return;

    switch (voiceState) {
      case 'idle':
        initializeVoiceRecognition();
        break;
      case 'recording':
        stopRecording();
        break;
      case 'error':
        dispatch(resetVoice());
        break;
      default:
        // Do nothing for loading, transcribing, processing states
        break;
    }
  }, [voiceState, disabled, initializeVoiceRecognition, stopRecording, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [mediaRecorder, mediaStream]);

  // Monitor session state for corruption and auto-recover during voice operations
  useEffect(() => {
    // If we're in the middle of a voice operation and session gets corrupted, auto-recover
    if ((voiceState === 'recording' || voiceState === 'transcribing' || voiceState === 'processing') && 
        !session.user_id && auth0IsAuthenticated && auth0User?.sub) {
      
      console.warn('🔐 VoiceButton: Session corruption detected during voice operation, auto-recovering:', {
        voiceState,
        sessionUserId: session.user_id,
        auth0UserId: auth0User.sub,
        sessionState: session
      });
      
      // Automatic recovery during voice operations
      recoverAuthentication();
    }
  }, [session.user_id, voiceState, auth0IsAuthenticated, auth0User, recoverAuthentication]);

  // Render voice button icon based on state
  const renderVoiceIcon = () => {
    switch (voiceState) {
      case 'loading':
        return <CircularProgress size={20} sx={{ color: 'orange' }} />;
      case 'ready':
        return <Mic sx={{ color: 'green' }} />;
      case 'recording':
        return isListening ? <Stop sx={{ color: 'red' }} /> : <Mic sx={{ color: 'red' }} />;
      case 'transcribing':
        return <CircularProgress size={20} sx={{ color: 'blue' }} />;
      case 'processing':
        return <VolumeUp sx={{ color: 'blue' }} />;
      case 'error':
        return <MicOff sx={{ color: 'red' }} />;
      default:
        return <Mic sx={{ color: isVoiceSupported ? 'inherit' : 'grey', fontSize: '19px' }} />;
    }
  };

  const getTooltipText = () => {
    if (!isVoiceSupported) return 'Voice input not supported';
    
    // Check authentication with fallback to Auth0
    const hasAuth = session.user_id || (auth0IsAuthenticated && auth0User?.sub);
    if (!hasAuth || !originalSessionId) return 'Please wait for authentication to complete';
    
    if (voiceError) return `Error: ${voiceError}`;
    if (statusMessage) return statusMessage;
    
    switch (voiceState) {
      case 'idle':
        return 'Click to start voice input';
      case 'loading':
        return 'Loading voice recognition...';
      case 'ready':
        return 'Ready to record';
      case 'recording':
        return 'Recording... Click to stop';
      case 'transcribing':
        return 'Converting speech to text...';
      case 'processing':
        return 'Sending to AI...';
      case 'error':
        return 'Click to retry';
      default:
        return 'Voice input';
    }
  };

  // Check authentication with fallback to Auth0
  const hasAuthentication = session.user_id || (auth0IsAuthenticated && auth0User?.sub);
  const isButtonDisabled = disabled || !isVoiceSupported || !hasAuthentication || !originalSessionId || ['loading', 'transcribing', 'processing'].includes(voiceState);

  return (
    <Box display="flex" alignItems="center" sx={{ mr: 1 }}
    onClick={handleVoiceButtonClick}
    >
        <Box 
        className='flex items-center border cursor-pointer'
          style={{
            borderColor: alpha(theme.palette.text.secondary, 0.2),
            borderRadius: '35px',
            padding: '7px 9px',
            backgroundColor: alpha(theme.palette.background.paper, 1),
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            gap: '2px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          sx={{
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
              borderColor: alpha(theme.palette.primary.main, 0.3),
              transform: 'scale(1.02)',
            },
            '&:active': {
              backgroundColor: alpha(theme.palette.action.selected, 0.2),
              transform: 'scale(0.98)',
            },
          }}
            > 
          <IconButton
            disabled={isButtonDisabled}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: voiceState === 'recording' 
                  ? 'rgba(244, 67, 54, 0.08)' 
                  : 'action.hover',
                '& .MuiSvgIcon-root': {
                  transform: 'scale(1.1)',
                },
              },
              '&:active': {
                backgroundColor: voiceState === 'recording' 
                  ? 'rgba(244, 67, 54, 0.12)' 
                  : 'action.selected',
              },
              '&:disabled': {
                backgroundColor: 'transparent',
                color: 'action.disabled',
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: '50%',
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {renderVoiceIcon()}
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>Voice</Typography>
                  </Box>
      
      {/* Real-time transcription display */}
      {transcriptionText && voiceState === 'recording' && (
        <Box sx={{ ml: 1, maxWidth: 200, overflow: 'hidden' }}>
          <Typography 
            variant="caption" 
            sx={{ 
              fontStyle: 'italic',
              color: 'text.secondary',
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            "{transcriptionText}"
          </Typography>
        </Box>
      )}
      
      {/* Error display */}
      {voiceError && voiceState === 'error' && (
        <Box sx={{ ml: 1, maxWidth: 300 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'error.main',
              fontSize: '0.7rem'
            }}
          >
            {voiceError}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default VoiceButton; 